import axios from 'axios';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

/**
 * Cliente del endpoint de CAC / CLTV.
 *
 * A diferencia de los servicios más antiguos del admin, este endpoint responde
 * con el envelope `{ data, meta }` y no con `{ status: 'ok', data }`: el éxito
 * se determina por el código HTTP, no por un campo del body.
 *
 * El churn ya no viaja en el body: se calcula de las bajas registradas. La
 * conversión de fracción a porcentaje sigue viviendo acá porque la lectura lo
 * devuelve como fracción y la vista lo muestra en porcentaje.
 */
export default class CacCltvService {
  static getHeaders() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    return { Authorization: `Bearer ${accessToken}` };
  }

  /**
   * Obtiene CAC, CLTV y el ratio CLTV/CAC por línea de negocio para un año.
   * @param {number} year - Año calendario.
   * @returns {Promise<{status: string, data?: object}>}
   */
  static async getMetrics(year) {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);

      const response = await axios.get(
        `${adminUrl}/cac-cltv?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return { status: 'error' };
    }
  }

  /**
   * Guarda los parámetros cargados a mano para un año.
   * @param {number} year - Año calendario.
   * @param {Array<{lineaNegocio: string, acquisitionCostUSD: number}>} parameters
   *   Costo anual de adquisición en USD. El churn ya no se carga: se mide de
   *   los contratos.
   * @returns {Promise<{status: string, data?: object}>}
   */
  static async saveParameters(year, parameters) {
    try {
      const body = {
        year,
        parameters: parameters.map((parameter) => ({
          lineaNegocio: parameter.lineaNegocio,
          acquisitionCostUSD: Number(parameter.acquisitionCostUSD)
        }))
      };

      const response = await axios.post(`${adminUrl}/cac-cltv/parameters`, body, {
        headers: this.getHeaders()
      });

      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return { status: 'error' };
    }
  }

  /**
   * Lista los contratos detrás del conteo de una tarjeta.
   * @param {number} year - Año calendario.
   * @param {string} lineaNegocio - Línea de la tarjeta que se está abriendo.
   * @param {'new'|'renewed'} kind - Adquisiciones o renovaciones excluidas.
   * @returns {Promise<{status: string, data?: object}>}
   */
  static async getContracts(year, lineaNegocio, kind) {
    try {
      const params = new URLSearchParams({ lineaNegocio, kind });
      if (year) params.append('year', year);

      const response = await axios.get(
        `${adminUrl}/cac-cltv/contracts?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return { status: 'error' };
    }
  }

  /** Convierte una fracción del backend (0.15) al porcentaje que muestra la UI (15). */
  static rateToPct(rate) {
    if (rate === null || rate === undefined) return null;
    return Number((Number(rate) * 100).toFixed(2));
  }
}
